import React from 'react';
import { Link } from 'react-router-dom';
import { Package, TrendingDown, CheckCircle, Sparkles } from 'lucide-react';
import { BULK_TIERS } from '../utils/pricing';

export const BulkOrders = () => {
  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 fade-in" data-testid="bulk-orders-page">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
            Bulk Orders
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight text-brand-green-dark mb-6">
            Buy More,
            <br />
            <span className="italic text-brand-green">Save More</span>
          </h1>
          <p className="font-body text-base sm:text-lg leading-relaxed font-light text-brand-green-dark/80 max-w-2xl mx-auto">
            Perfect for weddings, events, corporate gifting, and celebrations across Hyderabad.
            Enjoy tiered discounts on every A-Grade lily order — automatically applied at checkout.
          </p>
        </div>

        {/* Pricing tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {BULK_TIERS.map((tier, idx) => (
            <div
              key={`page-tier-${tier.discount}`}
              className={`p-10 rounded-sm text-center gold-border-glow smooth-transition ${
                idx === 2 ? 'bg-brand-green text-brand-cream' : 'bg-brand-blush/25'
              }`}
              data-testid={`bulk-page-tier-${tier.discount}`}
            >
              <div className="flex justify-center mb-6">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  idx === 2 ? 'bg-brand-gold/30' : 'bg-brand-gold/15'
                }`}>
                  <Package className="w-8 h-8 text-brand-gold" strokeWidth={1.5} />
                </div>
              </div>

              <p className={`font-heading text-6xl mb-2 ${idx === 2 ? 'text-brand-cream' : 'text-brand-green'}`}>
                {tier.discount}%
              </p>
              <p className={`uppercase tracking-[0.2em] text-xs font-bold mb-4 ${
                idx === 2 ? 'text-brand-gold' : 'text-brand-gold'
              }`}>
                Off Per Stem
              </p>
              <p className={`font-body text-lg mb-3 ${
                idx === 2 ? 'text-brand-cream' : 'text-brand-green-dark'
              }`}>
                {tier.label}
              </p>
              <p className={`font-body text-sm ${
                idx === 2 ? 'text-brand-cream/80' : 'text-brand-green-dark/60'
              }`}>
                Discount auto-applied at checkout
              </p>
            </div>
          ))}
        </div>

        {/* Example pricing */}
        <div className="bg-brand-sage/20 gold-border-glow p-12 rounded-sm mb-16">
          <h2 className="font-heading text-3xl font-light text-brand-green-dark text-center mb-8">
            Example: Ivory Elegance at ₹200/stem
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="uppercase tracking-[0.2em] text-xs text-brand-gold font-bold mb-2">
                1–9 stems
              </p>
              <p className="font-heading text-3xl text-brand-green-dark mb-2">₹200</p>
              <p className="font-body text-xs text-brand-green-dark/60">per stem</p>
            </div>

            <div className="text-center">
              <p className="uppercase tracking-[0.2em] text-xs text-brand-gold font-bold mb-2">
                10–24 stems
              </p>
              <p className="font-heading text-3xl text-brand-green mb-2">₹180</p>
              <p className="font-body text-xs text-brand-green-dark/60">per stem · 10% off</p>
            </div>

            <div className="text-center">
              <p className="uppercase tracking-[0.2em] text-xs text-brand-gold font-bold mb-2">
                25–49 stems
              </p>
              <p className="font-heading text-3xl text-brand-green mb-2">₹170</p>
              <p className="font-body text-xs text-brand-green-dark/60">per stem · 15% off</p>
            </div>

            <div className="text-center">
              <p className="uppercase tracking-[0.2em] text-xs text-brand-gold font-bold mb-2">
                50+ stems
              </p>
              <p className="font-heading text-3xl text-brand-green mb-2">₹160</p>
              <p className="font-body text-xs text-brand-green-dark/60">per stem · 20% off</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div className="flex gap-4 items-start bg-brand-cream gold-border-glow p-6 rounded-sm">
            <CheckCircle className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <p className="font-heading text-xl text-brand-green-dark mb-2">Perfect for Events</p>
              <p className="font-body text-sm text-brand-green-dark/70">
                Weddings, receptions, corporate events, and celebrations. Order in bulk and save significantly.
              </p>
            </div>
          </div>

          <div className="flex gap-4 items-start bg-brand-cream gold-border-glow p-6 rounded-sm">
            <Sparkles className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <p className="font-heading text-xl text-brand-green-dark mb-2">Same A-Grade Quality</p>
              <p className="font-body text-sm text-brand-green-dark/70">
                Every stem — whether one or one hundred — meets our premium A-grade standards with beautiful aroma.
              </p>
            </div>
          </div>

          <div className="flex gap-4 items-start bg-brand-cream gold-border-glow p-6 rounded-sm">
            <TrendingDown className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <p className="font-heading text-xl text-brand-green-dark mb-2">Automatic Savings</p>
              <p className="font-body text-sm text-brand-green-dark/70">
                No promo codes needed. The discount tier applies automatically based on your order quantity.
              </p>
            </div>
          </div>

          <div className="flex gap-4 items-start bg-brand-cream gold-border-glow p-6 rounded-sm">
            <Package className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <p className="font-heading text-xl text-brand-green-dark mb-2">2-Day Delivery</p>
              <p className="font-body text-sm text-brand-green-dark/70">
                Even large orders arrive within 2 business days across Hyderabad. Fresh and on time.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Link
            to="/products"
            className="inline-block bg-brand-green text-brand-cream px-12 py-4 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
            data-testid="shop-bulk-cta"
          >
            Start Bulk Order
          </Link>
        </div>
      </div>
    </div>
  );
};
